import React, { useEffect, useState } from 'react'
import { Headset, LogOut, ShieldAlert } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import axios from 'api/axiosConfig'

type StoredSupportSession = {
  id: string
  mode: 'support'
  expires_at?: string | null
  tenant_name?: string | null
}

function readSession(): StoredSupportSession | null {
  try {
    const raw = localStorage.getItem('authUser')
    if (!raw) return null
    const parsed = JSON.parse(raw) as { support_session?: StoredSupportSession }
    return parsed.support_session?.mode === 'support' ? parsed.support_session : null
  } catch {
    return null
  }
}

export const SupportSessionBootstrap: React.FC = () => {
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    const url = new URL(window.location.href)
    const grant = url.searchParams.get('support_grant')
    const tenantSlug = url.searchParams.get('tenant_slug')
    if (!grant || !tenantSlug || localStorage.getItem('support_exchange_in_progress') === '1') return

    localStorage.setItem('support_exchange_in_progress', '1')
    localStorage.setItem('tenant_slug', tenantSlug)
    axios.post('/support-sessions/exchange', { grant })
      .then((response) => {
        const data = response.data?.data as {
          token: string
          tenant_slug: string
          session: StoredSupportSession
          user: Record<string, unknown>
        }
        localStorage.setItem('tenant_slug', data.tenant_slug)
        localStorage.setItem('authUser', JSON.stringify({
          user: data.user,
          token: data.token,
          tenant_slug: data.tenant_slug,
          support_session: data.session,
        }))
        url.searchParams.delete('support_grant')
        url.searchParams.delete('tenant_slug')
        window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`)
        window.dispatchEvent(new Event('auth:updated'))
        window.dispatchEvent(new Event('support-session:started'))
      })
      .catch(() => {
        setMessage('تعذر بدء جلسة الدعم. قد يكون الرابط منتهيًا أو مستخدمًا من قبل.')
        localStorage.removeItem('tenant_slug')
      })
      .finally(() => localStorage.removeItem('support_exchange_in_progress'))
  }, [])

  if (!message) return null
  return <div className="fixed inset-x-3 top-3 z-[100] rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 shadow-lg">{message}</div>
}

export const SupportSessionBanner: React.FC = () => {
  const navigate = useNavigate()
  const [session, setSession] = useState<StoredSupportSession | null>(() => readSession())
  const [closing, setClosing] = useState(false)

  useEffect(() => {
    const sync = () => setSession(readSession())
    window.addEventListener('auth:updated', sync)
    window.addEventListener('support-session:started', sync)
    return () => {
      window.removeEventListener('auth:updated', sync)
      window.removeEventListener('support-session:started', sync)
    }
  }, [])

  if (!session) return null

  const endSession = async () => {
    setClosing(true)
    try {
      await axios.post('/support-sessions/end')
    } catch {
      /* The token may already be expired; local cleanup still ends the visible session. */
    } finally {
      localStorage.removeItem('authUser')
      localStorage.removeItem('tenant_slug')
      localStorage.removeItem('support_exchange_in_progress')
      window.dispatchEvent(new Event('auth:unauthorized'))
      navigate('/login', { replace: true })
    }
  }

  return (
    <div dir="rtl" className="sticky top-0 z-[60] border-b border-amber-300 bg-amber-50 px-3 py-2 text-amber-950 shadow-sm">
      <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center justify-between gap-2 text-xs sm:text-sm">
        <div className="flex min-w-0 items-center gap-2">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span className="font-semibold">وضع جلسة دعم</span>
          <span className="hidden min-w-0 truncate text-amber-800 sm:inline">{session.tenant_name ? `· ${session.tenant_name}` : '· دخول موثق من فريق المنصة'}</span>
        </div>
        <button type="button" onClick={endSession} disabled={closing} className="inline-flex min-h-8 items-center gap-1.5 rounded-lg border border-amber-400 bg-amber-100 px-2.5 py-1.5 font-semibold transition hover:bg-amber-200 disabled:opacity-60">
          {closing ? <Headset className="h-3.5 w-3.5" /> : <LogOut className="h-3.5 w-3.5" />}
          {closing ? 'جاري الإنهاء...' : 'إنهاء جلسة الدعم'}
        </button>
      </div>
    </div>
  )
}
